import type { EvalTaskSpec } from '@sbc/domain-models/agents';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';

import { describeTask, parseTaskYaml } from './task-yaml';

interface TaskEditorProps {
  initialText: string;
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  onSubmit: (spec: EvalTaskSpec) => void;
  onCancel: () => void;
}

export function TaskEditor({
  initialText,
  submitting,
  submitLabel,
  error,
  onSubmit,
  onCancel,
}: TaskEditorProps) {
  const [text, setText] = useState(initialText);
  const parsed = parseTaskYaml(text);

  const handleSubmit = () => {
    if (parsed.ok) {
      onSubmit(parsed.spec);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What this task does</CardTitle>
        </CardHeader>
        <CardContent>
          {parsed.ok ? (
            <ul className="space-y-1 text-sm">
              {describeTask(parsed.spec).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : (
            <pre className="text-sm whitespace-pre-wrap text-[var(--color-destructive)]">
              {parsed.error}
            </pre>
          )}
        </CardContent>
      </Card>

      <Textarea
        className="min-h-80 font-mono text-sm"
        spellCheck={false}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="button" onClick={handleSubmit} disabled={submitting || !parsed.ok}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
